class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
      cartElement.innerHTML = newCartContent;

      if (event.detail && event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }
}
customElements.define('cart-addon-modal', CartAddonModal);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.closest('quantity-input')) {
        FoxTheme.utils.debounce(this.onChange(event), 300);
      }
    });
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }
    alert(errors);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        if (event.cart.item_count > 0) {
          mainCart.closest('.cart').classList.remove('is-empty');
        } else {
          mainCart.closest('.cart').classList.add('is-empty');
        }
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }
}
customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.form__message'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest('.cart-addons-drawer');
  }

  get cartDiscountsEl() {
    return document.querySelector('.cart__discounts');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('btn--loading');

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.cart__discounts',
      item: '.discount',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('btn--loading');

    this.discounts = [];

    const thisItem = this.closest('li');
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };
}
customElements.define('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    this.carousel = false;
    this.isLoading = false;

    this.classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
      loading: 'is-loading',
    };

    // Handle Safari separately
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    if (isSafari) {
      this.init();
    } else {
      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  disconnectedCallback() {
    // Cleanup
    this.destroyCarousel();
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.removeEventListener('change', this.handleCarousel.bind(this));
  }

  init() {
    if (this.isLoading || !this.dataset.url) return;

    this.isLoading = true;
    this.classList.add(this.classes.loading);

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.addEventListener('change', this.handleCarousel.bind(this));
          this.handleCarousel();
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section')?.remove();
        }
      })
      .catch((error) => {
        console.error('Error loading recommendations:', error);
        this.classList.add('has-error');
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove(this.classes.loading);
      });
  }

  initCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }

    if (typeof this.carousel !== 'object') {
      try {
        this.container.classList.add(this.classes.swiper);
        this.wrapper.classList.remove(this.classes.grid);
        this.wrapper.classList.add(this.classes.swiperWrapper);

        let options = {
          slidesPerView: this.columns,
          spaceBetween: 8,
          loop: false,
          navigation: {
            nextEl: this.sliderNext,
            prevEl: this.sliderPrev,
          },
        };

        const paginationEl = this.querySelector('.swiper-pagination');
        if (paginationEl) {
          options = {
            ...options,
            pagination: {
              el: paginationEl,
              clickable: true,
              type: 'fraction',
            },
          };
        }

        this.carousel = new FoxTheme.Carousel(this.slideContainer, options);

        if (this.carousel) {
          this.carousel.init();
          this.calcNavButtonsPosition();
        }
      } catch (error) {
        console.error('Error initializing carousel:', error);
        this.destroyCarousel();
      }
    }
  }

  handleCarousel() {
    const containerEl = this.container;
    this.columns = containerEl.dataset.columns;
    this.enableSliderMobile = containerEl.dataset.enableSliderMobile === 'true';

    if (FoxTheme.config.mqlMobile) {
      this.enableSliderMobile ? this.initCarousel() : this.destroyCarousel();
    } else {
      this.initCarousel();
    }
  }

  destroyCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }
    this.container?.classList.remove(this.classes.swiper);
    this.wrapper?.classList.remove(this.classes.swiperWrapper);
    this.wrapper?.classList.add(this.classes.grid);

    if (typeof this.sliderInstance !== 'object') return;
    this.carousel.slider.destroy();
    this.carousel = false;
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get container() {
    return this.querySelector('.cart-drawer-products-recommendation__container');
  }

  get wrapper() {
    return this.querySelector('.cart-drawer-products-recommendation__wrapper');
  }
}
customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

class CartOfferTiersMulti extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      headerRemainingAmount: '[data-header-remaining-amount]',
      messageHeader: '.cart-offer-tiers__message-text',
      progressFill: '[data-progress-fill]',
      milestones: '.cart-offer-tiers__milestone',
    };
    this.maxThreshold = Number(this.dataset.maxThreshold) || 0;
    this.progressFill = this.querySelector(this.selectors.progressFill);
    this.milestones = this.querySelectorAll(this.selectors.milestones);
    this.money_format = window.FoxTheme.settings.moneyFormat;
    this.FREE_PRODUCT_PROPERTY = '_cart_offer_tier_free_product';
    this.isProcessing = false;
  }

  connectedCallback() {
    console.log('[CartOfferTiersMulti] Element connected', {
      cartTotal: this.dataset.cartTotal,
      maxThreshold: this.dataset.maxThreshold,
      milestonesCount: this.milestones.length
    });
    const cartTotal = Number(this.dataset.cartTotal) || 0;
    this.updateOfferTiers(cartTotal);
    document.addEventListener('cart:updated', (event) => {
      console.log('[CartOfferTiersMulti] Cart updated event received', event.detail);
      const cartData = event.detail.cart;
      // Prefer original_total_price (before automatic discounts). Fall back to subtotal, then total.
      let amount = 0;
      if (typeof cartData.original_total_price === 'number') {
        amount = cartData.original_total_price;
      } else if (typeof cartData.items_subtotal_price === 'number') {
        amount = cartData.items_subtotal_price;
      } else if (typeof cartData.total_price === 'number') {
        amount = cartData.total_price;
      }

      console.log('[CartOfferTiersMulti] Cart amount for update:', {
        original_total_price: cartData.original_total_price,
        items_subtotal_price: cartData.items_subtotal_price,
        total_price: cartData.total_price,
        total_discount: cartData.total_discount,
        amount,
        item_count: cartData.item_count
      });

      if (!amount && cartData.items_subtotal_price === undefined) {
        console.warn('[CartOfferTiersMulti] items_subtotal_price not found in cart data');
      }

      this.updateOfferTiers(amount, cartData);
    });
  }

  getEligibleCartAmount(cartData, fallbackAmount = 0) {
    let amount = typeof fallbackAmount === 'number' ? fallbackAmount : 0;

    if (cartData) {
      if (typeof cartData.original_total_price === 'number') {
        amount = cartData.original_total_price;
      } else if (typeof cartData.items_subtotal_price === 'number') {
        amount = cartData.items_subtotal_price;
      } else if (typeof cartData.total_price === 'number') {
        amount = cartData.total_price;
      }

      if (Array.isArray(cartData.items)) {
        let subscriptionTotal = 0;
        cartData.items.forEach((item) => {
          if (item && item.selling_plan_allocation) {
            let linePrice = 0;
            if (typeof item.original_line_price === 'number') {
              linePrice = item.original_line_price;
            } else if (typeof item.line_price === 'number') {
              linePrice = item.line_price;
            } else if (typeof item.final_line_price === 'number') {
              linePrice = item.final_line_price;
            }
            subscriptionTotal += linePrice;
          }
        });
        amount -= subscriptionTotal;
      }
    }

    if (amount < 0 || Number.isNaN(amount)) {
      amount = 0;
    }

    return amount;
  }

  async updateOfferTiers(amount, cartData = null) {
    console.log('[CartOfferTiersMulti] updateOfferTiers called', { amount, cartData: !!cartData });
    
    // Always fetch cart data to check for free products
    if (!cartData) {
      try {
        const cartResponse = await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() });
        cartData = await cartResponse.json();
        console.log('[CartOfferTiersMulti] Fetched cart data', cartData);
      } catch (error) {
        console.error('[CartOfferTiersMulti] Error fetching cart:', error);
        return;
      }
    }

    const eligibleAmount = this.getEligibleCartAmount(cartData, amount);
    console.log('[CartOfferTiersMulti] Eligible amount after excluding subscriptions:', {
      originalAmount: amount,
      eligibleAmount,
    });

    if (eligibleAmount > 0) {
      this.hidden = false;
    } else {
      this.hidden = true;
      // If cart is empty, remove all free products if they exist
      await this.removeAllFreeProducts(cartData);
      return;
    }

    const cartTotal = eligibleAmount / 100;
    console.log('[CartOfferTiersMulti] Cart total:', cartTotal, 'Max threshold:', this.maxThreshold);

    // Sort milestones by threshold
    const sortedMilestones = Array.from(this.milestones).sort((a, b) => {
      return Number(a.dataset.tierThreshold) - Number(b.dataset.tierThreshold);
    });

    console.log('[CartOfferTiersMulti] Milestones found:', sortedMilestones.length);

    // Update progress fill
    if (this.progressFill && this.maxThreshold > 0) {
      const fillPercent = Math.min((cartTotal / this.maxThreshold) * 100, 100);
      this.progressFill.style.width = `${fillPercent}%`;
      console.log('[CartOfferTiersMulti] Progress fill:', fillPercent + '%');
    }

    // Update each milestone and handle free products
    sortedMilestones.forEach((milestone, index) => {
      const tierThreshold = Number(milestone.dataset.tierThreshold) || 0;
      const freeProductId = milestone.dataset.freeProductId ? Number(milestone.dataset.freeProductId) : null;
      const isActive = cartTotal >= tierThreshold;

      console.log(`[CartOfferTiersMulti] Tier ${index + 1}:`, {
        threshold: tierThreshold,
        isActive,
        freeProductId,
        cartTotal
      });

      // Update active state
      if (isActive) {
        milestone.classList.add('active');
      } else {
        milestone.classList.remove('active');
      }

      // Handle free product add/remove automatically
      if (freeProductId && cartData) {
        const hasFreeProduct = this.hasFreeProductInCart(cartData, freeProductId);
        console.log(`[CartOfferTiersMulti] Tier ${index + 1} free product check:`, {
          freeProductId,
          hasFreeProduct,
          isActive
        });

        if (isActive && !hasFreeProduct) {
          // Threshold reached - add free product automatically
          console.log(`[CartOfferTiersMulti] Adding free product for tier ${index + 1}:`, freeProductId);
          this.addFreeProduct(freeProductId);
        } else if (!isActive && hasFreeProduct) {
          // Threshold dropped below - remove free product automatically
          console.log(`[CartOfferTiersMulti] Removing free product for tier ${index + 1}:`, freeProductId);
          this.removeFreeProduct(cartData, freeProductId);
        }
      } else if (freeProductId) {
        console.log(`[CartOfferTiersMulti] Tier ${index + 1} has free product ID but no cartData`);
      } else {
        console.log(`[CartOfferTiersMulti] Tier ${index + 1} has no free product configured`);
      }
    });

    // Update message header
    this.updateMessageHeader(cartTotal, sortedMilestones);
  }

  updateMessageHeader(cartTotal, sortedMilestones) {
    const messageHeaderEl = this.querySelector(this.selectors.messageHeader);
    if (!messageHeaderEl) return;

    let activeTierIndex = -1;
    for (let i = 0; i < sortedMilestones.length; i++) {
      const threshold = Number(sortedMilestones[i].dataset.tierThreshold) || 0;
      if (cartTotal >= threshold) {
        activeTierIndex = i;
      } else {
        break;
      }
    }

    console.log('[CartOfferTiersMulti] updateMessageHeader:', {
      cartTotal,
      activeTierIndex,
      totalMilestones: sortedMilestones.length
    });

    if (activeTierIndex >= 0 && activeTierIndex < sortedMilestones.length - 1) {
      // Show "Add X more to get [next tier]"
      const nextMilestone = sortedMilestones[activeTierIndex + 1];
      const nextThreshold = Number(nextMilestone.dataset.tierThreshold) || 0;
      const remaining = (nextThreshold - cartTotal) * 100;
      const nextTierLabel = nextMilestone.querySelector('.cart-offer-tiers__milestone-label')?.textContent || 'reward';

      messageHeaderEl.innerHTML = `Add items worth <strong data-header-remaining-amount>${FoxTheme.Currency.formatMoney(remaining, this.money_format)}</strong> to get ${nextTierLabel}!`;
      
      console.log('[CartOfferTiersMulti] Updated message: Add X more for next tier');
    } else if (activeTierIndex >= 0) {
      // Show success message for final tier
      const lastMilestone = sortedMilestones[activeTierIndex];
      const lastTierLabel = lastMilestone.querySelector('.cart-offer-tiers__milestone-label')?.textContent || 'reward';
      messageHeaderEl.textContent = `You've qualified for ${lastTierLabel}!`;
      
      console.log('[CartOfferTiersMulti] Updated message: Qualified for final tier');
    } else {
      // Show initial message for first tier (no tiers active yet)
      const firstMilestone = sortedMilestones[0];
      const firstThreshold = Number(firstMilestone.dataset.tierThreshold) || 0;
      const remaining = (firstThreshold - cartTotal) * 100;
      const firstTierLabel = firstMilestone.querySelector('.cart-offer-tiers__milestone-label')?.textContent || 'reward';
      
      messageHeaderEl.innerHTML = `Add items worth <strong data-header-remaining-amount>${FoxTheme.Currency.formatMoney(remaining, this.money_format)}</strong> to get ${firstTierLabel}!`;
      
      console.log('[CartOfferTiersMulti] Updated message: Initial message for first tier');
    }
  }

  hasFreeProductInCart(cartData, freeProductId) {
    if (!cartData || !cartData.items || !freeProductId) {
      console.log('[CartOfferTiersMulti] hasFreeProductInCart check failed:', {
        hasCartData: !!cartData,
        hasItems: !!(cartData && cartData.items),
        freeProductId
      });
      return false;
    }

    const found = cartData.items.some(item => {
      // Check by variant ID and property
      if (item.variant_id === freeProductId) {
        // Check if it has the free product property to confirm it's the tier free product
        const hasProperty = item.properties && item.properties[this.FREE_PRODUCT_PROPERTY] === 'true';
        if (hasProperty) {
          console.log('[CartOfferTiersMulti] Found free product in cart:', {
            variantId: item.variant_id,
            line: item.line,
            properties: item.properties
          });
          return true;
        } else {
          console.log('[CartOfferTiersMulti] Variant found but missing property:', {
            variantId: item.variant_id,
            properties: item.properties,
            expectedProperty: this.FREE_PRODUCT_PROPERTY
          });
        }
      }
      return false;
    });

    console.log('[CartOfferTiersMulti] hasFreeProductInCart result:', {
      freeProductId,
      found,
      cartItems: cartData.items.map(i => ({ variant_id: i.variant_id, properties: i.properties }))
    });

    return found;
  }

  async addFreeProduct(freeProductId) {
    if (!freeProductId) {
      console.warn('[CartOfferTiersMulti] addFreeProduct called with no freeProductId');
      return;
    }
    
    if (this.isProcessing) {
      console.warn('[CartOfferTiersMulti] Already processing, skipping add');
      return;
    }

    console.log('[CartOfferTiersMulti] addFreeProduct START:', freeProductId);
    this.isProcessing = true;

    try {
      // Get sections to render like ProductForm and updateQuantity do
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const formData = new FormData();
      formData.append('id', freeProductId);
      formData.append('quantity', '1');
      formData.append('properties[_cart_offer_tier_free_product]', 'true');
      formData.append('sections', sectionsToBundle);
      formData.append('sections_url', window.location.pathname);

      // Use same config pattern as ProductForm
      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      console.log('[CartOfferTiersMulti] Sending add request:', {
        url: FoxTheme.routes.cart_add_url,
        variantId: freeProductId,
        sections: sectionsToBundle
      });

      const response = await fetch(`${FoxTheme.routes.cart_add_url}`, config);
      const parsedState = await response.json();

      console.log('[CartOfferTiersMulti] Add response:', parsedState);

      if (parsedState.status) {
        console.error('[CartOfferTiersMulti] Error adding free product:', parsedState.description, parsedState);
        this.isProcessing = false;
        return;
      }

      console.log('[CartOfferTiersMulti] ✅ Free product added successfully:', freeProductId);

      // Use sections from parsedState directly like ProductForm does
      const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
      cartJson['sections'] = parsedState['sections'] || {};

      // Update cart state using pubsub like ProductForm
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    } catch (error) {
      console.error('[CartOfferTiersMulti] ❌ Error adding free product:', error);
    } finally {
      this.isProcessing = false;
      console.log('[CartOfferTiersMulti] addFreeProduct END');
    }
  }

  async removeFreeProduct(cartData, freeProductId) {
    if (!freeProductId || this.isProcessing || !cartData || !cartData.items) return;

    console.log('[CartOfferTiersMulti] removeFreeProduct START:', freeProductId);
    this.isProcessing = true;

    try {
      // Find the free product in cart
      const freeProductItemIndex = cartData.items.findIndex(item => {
        return item.properties && item.properties[this.FREE_PRODUCT_PROPERTY] === 'true' && item.variant_id === freeProductId;
      });

      if (freeProductItemIndex === -1) {
        console.log('[CartOfferTiersMulti] Free product not found in cart:', freeProductId);
        this.isProcessing = false;
        return;
      }

      const freeProductItem = cartData.items[freeProductItemIndex];
      // Line is 1-based index for cart/change.js
      const itemLine = freeProductItem.line || (freeProductItemIndex + 1);

      console.log('[CartOfferTiersMulti] Removing free product:', {
        line: itemLine,
        index: freeProductItemIndex,
        variantId: freeProductId,
        item: freeProductItem
      });

      // Get sections to render like updateQuantity does
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      // Remove the item by setting quantity to 0
      const response = await fetch(`${FoxTheme.routes.cart_change_url}`, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({
          line: itemLine,
          quantity: 0,
          sections: sectionsToBundle,
        }),
      });

      const result = await response.json();

      if (result.status) {
        console.error('[CartOfferTiersMulti] Error removing free product:', result.description, result);
        this.isProcessing = false;
        return;
      }

      console.log('[CartOfferTiersMulti] ✅ Free product removed successfully:', freeProductId);

      // Update cart state using pubsub like updateQuantity does - use result directly
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: result });
    } catch (error) {
      console.error('[CartOfferTiersMulti] ❌ Error removing free product:', error);
    } finally {
      this.isProcessing = false;
      console.log('[CartOfferTiersMulti] removeFreeProduct END');
    }
  }

  async removeAllFreeProducts(cartData) {
    if (!cartData || !cartData.items) return;

    // Find all free product indices
    const freeProductIndices = [];
    cartData.items.forEach((item, index) => {
      if (item.properties && item.properties[this.FREE_PRODUCT_PROPERTY] === 'true') {
        freeProductIndices.push(index);
      }
    });

    if (freeProductIndices.length === 0) return;

    this.isProcessing = true;

    try {
      // Get sections to render
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      // Remove all free products by line number (1-based index)
      for (const index of freeProductIndices) {
        const item = cartData.items[index];
        const itemLine = item.line || (index + 1);
        
        await fetch(FoxTheme.routes.cart_change_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({
            line: itemLine,
            quantity: 0,
            sections: sectionsToBundle,
          }),
        });
      }

      // Refresh cart to update UI
      const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: cartJson,
          },
        })
      );
    } catch (error) {
      console.error('[CartOfferTiersMulti] Error removing all free products:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}
customElements.define('cart-offer-tiers-multi', CartOfferTiersMulti);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};
